import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, InfoTip, Notice } from '@rydres/ui';
import { api } from '../lib/api.js';
import { disablePush, enablePush, isIos, isStandalone, pushState, testPush, type PushState } from '../lib/push.js';

export function NotificationsSettings() {
  const navigate = useNavigate();
  const [state, setState] = useState<PushState | null>(null);
  const [devices, setDevices] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    setState(await pushState());
    api.call<{ devices: number }>('GET', '/driver/push').then((r) => setDevices(r.devices), () => undefined);
  };
  useEffect(() => void refresh(), []);
  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      setMsg(done);
    } catch {
      setMsg('That did not work. Try again.');
    } finally {
      setBusy(false);
      await refresh();
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/settings')} className="rounded-full bg-card2 p-2 px-3" aria-label="Back">‹</button>
        <h1 className="text-xl font-black">Notifications</h1>
        <InfoTip label="Notifications">You always get a text for new requests, confirmations and cancellations. Notifications add an instant alert on this phone, free and with the app closed.</InfoTip>
      </div>
      <Card title="This device">
        {state === null ? <p className="text-sm text-muted">Checking…</p> : (
          <div className="space-y-3 text-sm">
            <div>Status: <Badge value={state === 'on' ? 'on' : state === 'denied' ? 'blocked' : state} tone={state === 'on' ? 'ok' : state === 'denied' ? 'danger' : 'muted'} /></div>
            {state === 'needs-install' && (
              <Notice tone="info">On iPhone, notifications only work after you add RydRes to your Home Screen from Safari's Share menu. Then open it from there and come back to this screen.</Notice>
            )}
            {state === 'unsupported' && <Notice tone="info">This browser cannot show notifications.</Notice>}
            {state === 'denied' && <Notice tone="warn">Blocked in the phone's settings. Allow notifications for RydRes there, then reload.</Notice>}
            {state === 'off' && <Button disabled={busy} onClick={() => void run(enablePush, 'Notifications are on.')}>Turn on notifications</Button>}
            {state === 'on' && (
              <div className="flex flex-wrap gap-2">
                <Button small disabled={busy} onClick={() => void run(async () => { const n = await testPush(); if (!n) throw new Error('none'); }, 'Sent. It should appear in a moment.')}>Send a test</Button>
                <Button small variant="ghost" disabled={busy} onClick={() => void run(disablePush, 'Notifications are off on this device.')}>Turn off</Button>
              </div>
            )}
            {msg && <p className="text-xs text-muted">{msg}</p>}
            {!isStandalone() && isIos() && state === 'on' && <p className="text-xs text-muted">Tip: open RydRes from your Home Screen icon for the most reliable alerts.</p>}
          </div>
        )}
      </Card>
      {devices !== null && <p className="text-xs text-muted">{devices} device{devices === 1 ? '' : 's'} registered for notifications on your account.</p>}
    </div>
  );
}
